/**
 * DenetimFormuDoldurPage.jsx — denetim formu doldurma akışının tek ekranı
 * Route: /Management/Forms/doldur?tab=kontrol|uygunsuzluk|dof
 *
 * 2026-09-12'ye kadar "Denetim Formları" adında ayrı bir üst menüydü ve üç ayrı
 * menü girdisine dağılmıştı:
 *
 *   Kontrol Formu            → /FdsSaha/Formlar/denetim      (SahaFormFill levelId=1)
 *   Uygunsuzluk Formu        → /FdsSaha/Formlar/uygunsuzluk  (SahaUygunsuzlukList)
 *   Duz. Onl. Faaliyet (DÖF) → /FdsSaha/Formlar/dof          (SahaUygunsuzlukList levelFilter=3)
 *
 * Artık Form Yönetimi altında tek sayfa, üç sekme. Üç ekran da `embedded`
 * bayrağıyla kendi sayfa kabuğunu (page-content/Container/BreadCrumb) kapatıp
 * sadece içeriğini basar — bkz. Components/Common/EmbeddablePage.jsx. İş mantığı
 * tek yerde kalıyor, kendi /FdsSaha rotaları da (saha ana sayfasındaki kısayollar
 * oradan geçiyor) çalışmaya devam ediyor.
 *
 * Kardeş sayfa: Denetim Kayıtları (/Management/Forms/instances) — doldurulmuş
 * formların listesi. Bu sayfa doldurma tarafı.
 *
 * Sekme seçimi `?tab=` ile URL'de tutuluyor. Bir kez açılan sekme DOM'da kalır
 * (TabContent gizler, unmount etmez) — böylece yarım kalan bir form sekme
 * değiştirince kaybolmaz; hiç açılmamış sekme ise backend'e gitmez.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container, Card, Nav, NavItem, NavLink, TabContent, TabPane,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import SahaFormFill from "../../FdsSaha/SahaFormFill";
import SahaUygunsuzlukList from "../../FdsSaha/SahaUygunsuzlukList";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const TABS = [
  { key: "kontrol",     get label() { return i18n.t("formlar:denetimFormuDoldur.kontrolFormu"); },            icon: "bx-clipboard" },
  { key: "uygunsuzluk", get label() { return i18n.t("formlar:denetimFormuDoldur.uygunsuzlukFormu"); },        icon: "bx-error-circle" },
  { key: "dof",         get label() { return i18n.t("formlar:denetimFormuDoldur.duzOnlFaaliyetDof"); }, icon: "bx-wrench" },
];

export default function DenetimFormuDoldurPage() {
  const { t: tx } = useTranslation("formlar");
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam  = searchParams.get("tab");
  const activeTab = TABS.some((t) => t.key === tabParam) ? tabParam : "kontrol";

  // Açılmış sekmeler mount edilmiş kalsın (yarım form kaybolmasın);
  // hiç açılmamış sekme render edilmez ki üç ekran birden veri çekmesin.
  const [visited, setVisited] = useState(() => ({ [activeTab]: true }));
  const isMounted = (key) => key === activeTab || visited[key];

  useEffect(() => { document.title = i18n.t("formlar:denetimFormuDoldur.denetimFormuDoldurFds"); }, []);

  const setTab = (key) => {
    setVisited((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("denetimFormuDoldur.denetimFormuDoldur")} pageTitle={tx("denetimFormuDoldur.formYonetimi")} />

        {/* Sekme çubuğu — Velzon "nav-tabs-custom" deseni */}
        <Card className="mb-0">
          <Nav tabs className="nav-tabs-custom border-bottom-0 px-3 pt-2">
            {TABS.map((t) => (
              <NavItem key={t.key}>
                <NavLink
                  href="#"
                  className={`fw-semibold ${activeTab === t.key ? "active" : ""}`}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => { e.preventDefault(); setTab(t.key); }}
                >
                  <i className={`bx ${t.icon} me-1 align-middle`} />
                  {t.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </Card>

        <TabContent activeTab={activeTab} className="mt-3">
          <TabPane tabId="kontrol">
            {isMounted("kontrol") && (
              <SahaFormFill embedded levelId={1} title={tx("denetimFormuDoldur.denetimFormuDoldur")} />
            )}
          </TabPane>
          <TabPane tabId="uygunsuzluk">
            {isMounted("uygunsuzluk") && <SahaUygunsuzlukList embedded />}
          </TabPane>
          <TabPane tabId="dof">
            {isMounted("dof") && <SahaUygunsuzlukList embedded levelFilter={3} />}
          </TabPane>
        </TabContent>
      </Container>
    </div>
  );
}
